import { useMemo } from "react";

import {
    TimeseriesChart,
    FormatType,
    useAppPalette,
    ConfigOption,
    TimeseriesChartProps,
    useTimeWindow,
    Column
} from "@bridgesplit/ui";
import { getSamplingInterval } from "app/components/common/util";
import { bpsToUiDecimals, getUnixTs, lamportsToUiAmount, TIME } from "@bridgesplit/utils";
import {
    LendingVaultExpanded,
    useLatestCollateralYieldHistoryByMint,
    useLendingVaultHistoryQuery
} from "@bridgesplit/abf-react";
import { skipToken } from "@reduxjs/toolkit/dist/query";

export enum LendingVaultChartOpen {
    Apy = "APY",
    Deployed = "Deployed",
    Volume = "Volume",
    Utilization = "Utilization",
    Tvl = "TVL"
}

export type LendingVaultChartData = {
    date: number;
    apy: number | undefined;
    totalApy: number | undefined;
    externalRewardsApy: number | undefined;
    collateralYieldApy: number | undefined;
    deployed: number;
    volume: number;
    utilization: number;
    netAssetValue: number;
    activeLoanCount: number;
    normalizedActiveLoanCount: number | undefined;
};

const FALLBACK_DATA: LendingVaultChartData[] = [
    {
        date: getUnixTs(),
        apy: 0.15,
        totalApy: 0.18,
        externalRewardsApy: 0.0,
        collateralYieldApy: 0.0,
        deployed: 3399,
        volume: 500,
        utilization: 0.7,
        netAssetValue: 10000,
        activeLoanCount: 25,
        normalizedActiveLoanCount: undefined
    },
    {
        date: getUnixTs() + TIME.DAY,
        apy: 0.13,
        totalApy: 0.16,
        externalRewardsApy: 0.0,
        collateralYieldApy: 0.0,
        deployed: 10000,
        volume: 10000,
        utilization: 0.8,
        netAssetValue: 10000,
        activeLoanCount: 45,
        normalizedActiveLoanCount: undefined
    }
];

export function useLendingVaultChartData(
    vaultAddress: string | undefined,
    vaultExpanded: LendingVaultExpanded | undefined,
    enabledCharts?: LendingVaultChartOpen[]
) {
    const { secondary, primary, info, prime } = useAppPalette();

    const { data: history, isFetching: isHistoryFetching } = useLendingVaultHistoryQuery(
        vaultAddress
            ? {
                  vaultAddress,
                  timeLookback: TIME.MONTH * 3,
                  sampleIntervalSecs: getSamplingInterval(TIME.MONTH * 3)
              }
            : skipToken,
        { skip: !vaultAddress }
    );

    const { data: collateralYields } = useLatestCollateralYieldHistoryByMint(
        vaultExpanded?.vault.principalMint ? [vaultExpanded?.vault.principalMint] : []
    );

    const principalMint = vaultExpanded?.vault.principalMint;
    const rawCollateralYieldApy = collateralYields && principalMint ? collateralYields[principalMint] : undefined;

    const data = useMemo(() => {
        const decimals = vaultExpanded?.principalMetadata.decimals;

        if (!history?.length) {
            return FALLBACK_DATA;
        }

        const rawData = history.map((h): Omit<LendingVaultChartData, "normalizedActiveLoanCount"> => {
            const baseApy = bpsToUiDecimals(h.wAvgApy);
            const externalRewardsApy = h.externalRewardsApy ? bpsToUiDecimals(h.externalRewardsApy) : 0;
            const collateralYield = rawCollateralYieldApy ? rawCollateralYieldApy : 0;
            const totalApy = baseApy + externalRewardsApy + collateralYield;
            return {
                date: h.timestamp,
                apy: baseApy,
                totalApy: totalApy,
                collateralYieldApy: collateralYield,
                externalRewardsApy: externalRewardsApy,
                deployed: lamportsToUiAmount(h.principalDeployed, decimals),
                volume: lamportsToUiAmount(h.cumulativeLoanVolume, decimals),
                utilization: h.utilizationRate ?? 0,
                netAssetValue: lamportsToUiAmount(h.netAssetValue, decimals),
                activeLoanCount: h.activeLoanCount ?? 0
            };
        });

        // Normalize activeLoanCount to deployed scale for dual-axis display
        const maxDeployed = rawData.reduce((max, d) => Math.max(max, d.deployed), 0);
        const maxActiveLoanCount = rawData.reduce((max, d) => Math.max(max, d.activeLoanCount), 0);
        const normalizationFactor = maxDeployed / Math.max(maxActiveLoanCount, 1);

        return rawData.map(
            (d): LendingVaultChartData => ({
                ...d,
                normalizedActiveLoanCount: d.activeLoanCount !== 0 ? d.activeLoanCount * normalizationFactor : undefined
            })
        );
    }, [history, vaultExpanded, rawCollateralYieldApy]);

    const hasAnyRewards = useMemo(() => data.some((d) => d.externalRewardsApy && d.externalRewardsApy > 0), [data]);
    const hasAnyYield = useMemo(() => data.some((d) => d.collateralYieldApy && d.collateralYieldApy > 0), [data]);

    const chartConfigs = useMemo(() => {
        const enabledSet = enabledCharts && enabledCharts.length > 0 ? new Set(enabledCharts) : null;
        const configs: Record<string, ConfigOption<LendingVaultChartData>> = {};

        if (!enabledSet || enabledSet.has(LendingVaultChartOpen.Apy)) {
            const apySeries: ConfigOption<LendingVaultChartData>["series"] = [
                { dataKey: "apy", label: "Base APY", color: primary }
            ];
            if (hasAnyRewards || hasAnyYield) {
                apySeries.push({ dataKey: "totalApy", label: "Total APY", color: secondary });
                apySeries.push({
                    dataKey: "externalRewardsApy",
                    label: "Rewards APY",
                    deltaFill: {
                        valueSeries: "totalApy",
                        referenceSeries: "apy",
                        valueFillColor: prime
                    }
                });
            }
            configs[LendingVaultChartOpen.Apy] = {
                label: LendingVaultChartOpen.Apy,
                axisFormat: FormatType.Percent,
                series: apySeries
            };
        }

        if (!enabledSet || enabledSet.has(LendingVaultChartOpen.Deployed)) {
            configs[LendingVaultChartOpen.Deployed] = {
                label: LendingVaultChartOpen.Deployed,
                axisFormat: FormatType.Token,
                series: [
                    { dataKey: "deployed", label: "Principal Deployed", labelCondensed: "Deployed", color: primary },
                    { dataKey: "normalizedActiveLoanCount", label: "Active Loans", color: secondary, dash: true }
                ]
            };
        }

        if (!enabledSet || enabledSet.has(LendingVaultChartOpen.Volume)) {
            configs[LendingVaultChartOpen.Volume] = {
                label: LendingVaultChartOpen.Volume,
                axisFormat: FormatType.Token,
                series: [{ dataKey: "volume", label: "Cumulative Volume", labelCondensed: "Volume", color: primary }]
            };
        }

        if (!enabledSet || enabledSet.has(LendingVaultChartOpen.Utilization)) {
            configs[LendingVaultChartOpen.Utilization] = {
                label: LendingVaultChartOpen.Utilization,
                axisFormat: FormatType.Percent,
                series: [
                    { dataKey: "utilization", label: "Utilization Rate", labelCondensed: "Utilization", color: primary }
                ]
            };
        }

        if (!enabledSet || enabledSet.has(LendingVaultChartOpen.Tvl)) {
            configs[LendingVaultChartOpen.Tvl] = {
                label: LendingVaultChartOpen.Tvl,
                axisFormat: FormatType.Token,
                series: [
                    { dataKey: "netAssetValue", label: "Total Value Locked", labelCondensed: "TVL", color: primary }
                ]
            };
        }

        return configs;
    }, [enabledCharts, hasAnyRewards, hasAnyYield, primary, secondary, info, prime]);

    return {
        data,
        chartConfigs,
        loading: !history || isHistoryFetching
    };
}

export function getVaultChartConfig(
    selectedChart: LendingVaultChartOpen,
    chartData: ReturnType<typeof useLendingVaultChartData>
): TimeseriesChartProps<LendingVaultChartData> | undefined {
    const config = chartData.chartConfigs[selectedChart];
    if (!config) return undefined;
    return {
        chartId: `vault-${selectedChart}`,
        data: chartData.data,
        axisFormat: config.axisFormat,
        series: config.series,
        loading: chartData.loading
    };
}

// Simple wrapper for consumers that don't need stat tabs (e.g. manager view)
export type LendingVaultChartsProps = {
    vaultAddress: string | undefined;
    vaultExpanded: LendingVaultExpanded | undefined;
    periodStorageKey?: string;
    enabledCharts?: LendingVaultChartOpen[];
};

export function LendingVaultCharts({
    vaultAddress,
    vaultExpanded,
    enabledCharts,
    periodStorageKey
}: LendingVaultChartsProps) {
    const chartData = useLendingVaultChartData(vaultAddress, vaultExpanded, enabledCharts);
    const configOptions = useMemo(() => Object.values(chartData.chartConfigs), [chartData.chartConfigs]);
    const { period, setPeriod, xDomain } = useTimeWindow({ storageKey: periodStorageKey });

    return (
        <Column spacing={2}>
            <TimeseriesChart
                chartId={`vault-${vaultAddress}`}
                data={chartData.data}
                configOptions={configOptions}
                height={200}
                loading={chartData.loading}
                xDomain={xDomain}
                period={period}
                setPeriod={setPeriod}
            />
        </Column>
    );
}
